import React, { useMemo, useEffect } from "react"
import { Redirect } from "react-router"
import { Button, ModalPanel, Title, Text } from "dash-styled"
import { useBoolean } from "../../../Hooks/useBoolean"
import { DownloadRefHandler } from "../../../Components/Views/Dashboard/Common/DownloadRefHandler/DownloadRefHandler"
import { JobsListProps } from "../../../Components/Views/Dashboard/Sections/AppDetail/JobsList/JobsList"
import {
    decodeJobListParams,
    defaultJobSortExpression,
    defaultJobMessageSortExpression,
    APIJobQueryModel
} from "../../../Models/API/JobModel"
import { routePaths } from "../../"
import {
    decodeFilterAndSort,
    FilterAndSortExpression,
    encodeFilterAndSort
} from "../../../Models/Client/FilterAndSortExpression"
import { Result } from "../../../Models/Utility/Result"
import { useAppState } from "../../../Hooks/useReduxState"
import { MessagesListProvider } from "../../../Components/Views/Dashboard/Sections/AppDetail/JobsList/MessagesListProvider"
import { JobsListProvider } from "../../../Components/Views/Dashboard/Sections/AppDetail/JobsList/JobsListProvider"
import { SortExpression } from "../../../Models/Client/SortExpression"
import { useAppDetailsPaths } from "../../../Hooks/useAppDetailsPaths"
import { useDispatch } from "react-redux"
import { setExplanationModal } from "../../../Actions/Client/RouteInfoActions"
import { useExplanationModal } from "../../../Hooks/useRouteInfo"
import { ExplanationModalType } from "../../../Reducers/Client/RouteInfoReducer"
import { AppDetailRouteComponentProps } from "../../RouteProps"
import { AppAuthCheck } from "../../../Components/Common/AuthCheck/AppAuthCheck"

export interface AppDetailRouteParams
    extends FilterAndSortExpression<APIJobQueryModel> {
    /**
     * `true` to tell the intent to open the modal view of the first item of the list.
     * `false` by default.
     */
    openDetail?: boolean
    /**
     * download handle
     */
    d?: string
    /**
     * `true` to clear filter after closing modal. Necessary when modal is opened via 'Review results' button.
     * `false` by default.
     * Intended to be used along with `openDetail=true`.
     */
    clearFilterOnCloseModal?: boolean
}

type ComponentProps = Omit<
    JobsListProps,
    "appID" | "onVisibleItemsChanged" | "isReviewView"
> & {
    d: string | undefined
}

export function encodeAppDetailRouteParams(
    {
        openDetail,
        clearFilterOnCloseModal,
        ...filterAndSort
    }: AppDetailRouteParams,
    defaultSort: SortExpression<APIJobQueryModel> = defaultJobSortExpression
): URLSearchParams | undefined {
    let search = encodeFilterAndSort(filterAndSort, defaultSort)

    if (clearFilterOnCloseModal) {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("clearFilterOnCloseModal", "true")
    }

    if (openDetail) {
        if (search === undefined) {
            search = new URLSearchParams()
        }
        search.set("open", "true")
    }

    return search
}

export function decodeParams(
    orgUsername: string,
    search: URLSearchParams,
    isMessagePath = false
): Result<ComponentProps> {
    const filterAndSortDecodeResult = decodeFilterAndSort<APIJobQueryModel>(
        search,
        isMessagePath
            ? defaultJobMessageSortExpression
            : defaultJobSortExpression
    )
    if (filterAndSortDecodeResult.isError) {
        return filterAndSortDecodeResult
    }

    const decodedParams = decodeJobListParams(filterAndSortDecodeResult.value)
    if (decodedParams.isError) {
        return decodedParams
    }
    return Result.ok<ComponentProps>({
        orgUsername,
        filterAndSort: filterAndSortDecodeResult.value,
        params: decodedParams.value,
        jobID: search.get("open") === "true" ? null : undefined,
        selectedJob: search.get("job"),
        clearFilterOnCloseModal:
            search.get("clearFilterOnCloseModal") === "true",
        d: search.get("d") ?? undefined
    })
}

export function AppDetailRoute(
    props: AppDetailRouteComponentProps<{
        jobID?: string
        version?: string
        taskID?: string
    }>
) {
    const { appID, jobID, version, taskID, orgUsername } = props.match.params
    const isReviewView = props.location.pathname.includes(`${jobID}/review`)
    const dispatch = useDispatch()
    const [isMessages] = useAppDetailsPaths()

    const decoded = useMemo(
        () =>
            decodeParams(
                orgUsername,
                new URLSearchParams(props.location.search),
                isMessages
            ),
        [isMessages, orgUsername, props.location.search]
    )
    const { search } = useAppState(state => state.router.location)
    const selected = useMemo(() => {
        const s = search ? new URLSearchParams(search) : undefined
        if (!s) return
        const sel = s.get("selected")
        if (!sel) return
        const selectedItems = sel.slice(1, sel.length - 1).split(",")
        return selectedItems
    }, [search])

    const { visible: showExplanationModalState, type: explanationModalType } =
        useExplanationModal()
    const [
        showExplanationModal,
        setShowExplanationModalTrue,
        setShowExplanationModalFalse
    ] = useBoolean(false)

    useEffect(() => {
        if (showExplanationModalState) {
            setShowExplanationModalTrue()
            dispatch(setExplanationModal(false, explanationModalType))
        }
    }, [
        dispatch,
        explanationModalType,
        setShowExplanationModalTrue,
        showExplanationModalState
    ])

    const authCheckProps = {
        orgUsername,
        appID,
        scope: isMessages ? ("messages:view" as const) : ("job:view" as const)
    }

    if (decoded.isError) {
        return (
            <Redirect
                to={
                    jobID === undefined
                        ? routePaths.appDetail(orgUsername, appID)
                        : taskID === undefined
                        ? routePaths.jobsDetail(orgUsername, appID, jobID)
                        : routePaths.taskDetails(
                              orgUsername,
                              appID,
                              jobID,
                              version ?? "0",
                              taskID
                          )
                }
            />
        )
    }

    return (
        <AppAuthCheck {...authCheckProps}>
            <DownloadRefHandler
                downloadRef={decoded.value.d}
                appID={appID}
                redirectToOnClose={routePaths.appDetail(orgUsername, appID)}
            >
                {isMessages ? (
                    <MessagesListProvider
                        appID={appID}
                        {...decoded.value}
                        jobID={
                            decoded.value.jobID === undefined
                                ? jobID
                                : decoded.value.jobID
                        }
                        version={version}
                        taskID={taskID}
                        selected={selected}
                    />
                ) : (
                    <JobsListProvider
                        appID={appID}
                        {...decoded.value}
                        jobID={
                            decoded.value.jobID === undefined
                                ? jobID
                                : decoded.value.jobID
                        }
                        version={version}
                        taskID={taskID}
                        selected={selected}
                        isReviewView={isReviewView}
                    />
                )}

                {showExplanationModal && (
                    <ModalPanel
                        testId="dead-man-switch-modal"
                        onClose={setShowExplanationModalFalse}
                        className="max-w-2xl w-full"
                    >
                        <Title
                            variant="heading2"
                            weight="semibold"
                            component="h2"
                        >
                            {explanationModalType ===
                            ExplanationModalType.timeout
                                ? "Time ran out"
                                : "We lost connectivity"}
                        </Title>
                        <Text size="sm" color="gray">
                            {explanationModalType ===
                            ExplanationModalType.timeout
                                ? "Tasks expire after you have been inactive for 5 mins. You've lost your saved draft"
                                : "Tasks expire if you have connectivity problems for too long"}
                        </Text>
                        <Button
                            testId="dead-man-switch-button"
                            className="mt-7 mx-auto w-24 justify-center"
                            onClick={setShowExplanationModalFalse}
                            size="sm"
                        >
                            OK
                        </Button>
                    </ModalPanel>
                )}
            </DownloadRefHandler>
        </AppAuthCheck>
    )
}
